import {observable, action} from 'mobx';
import {DomainStore} from 'shared/store';
import {endpoints, types} from 'main/tools';
import Employee from 'main/stores/employees/Employee';
import {
  PerformanceReviewCycle,
  PerformanceReviewReview,
  PerformanceReviewReviewee
} from 'main/stores/performance_reviews';
import _ from 'lodash';
import moment from 'moment';

class AddRevieweeState {
  onHide;
  onSave;
  cycle;
  employees;
  revieweeEmployees;
  store = new DomainStore();

  @observable isOpen = false;
  @observable selectedReviewee = {};
  @observable selectedManagers = [];
  @observable selectedPeers = [];
  @observable errors = {};

  @action receiveProps({isOpen, onHide, cycle, onSave}) {
    this.isOpen = isOpen;
    this.onHide = onHide;
    this.onSave = onSave;
    this.cycle = cycle;
  }

  @action async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.PERFORMANCE_REVIEWS.CYCLE_WITH_REVIEWEES.with(this.cycle.id),
    ]);

    this.employees = this.store.getAllOrdered(types.EMPLOYEE, Employee);
    this.revieweeEmployees = this.store.getAllOrdered(types.EMPLOYEE, Employee);

    this.reviewees = this.store._getAll(
      types.PERFORMANCE_REVIEWS.REVIEWEE,
      PerformanceReviewReviewee
    );

    const reviewee_ids = _.map(this.reviewees, 'employee.id');
    this.revieweeEmployees = _.differenceWith(this.employees, reviewee_ids,
      ({ id }, reviewee_id) => id === reviewee_id
    );
  }

  @action updateReviewee(revieweeEmployee) {
    this.selectedReviewee = new PerformanceReviewReviewee({employee: revieweeEmployee, launchDate: moment()});

    this.selectedManager = null;
    this.selectedPeers = [];

    if (this.selectedReviewee.employee) {
      if (this.selectedReviewee.managerId) {
        this.selectedManager = _.find(this.employees, {id: this.selectedReviewee.employee.managerId});
      }

      this.employees = _.reject(
        this.store.getAllOrdered(types.EMPLOYEE, Employee),
        employee => employee.equals(this.selectedReviewee.employee)
      );
    }
  }

  @action addPeer(peer) {
    this.selectedPeers.push(peer);
  }

  @action removePeer(peer) {
    this.selectedPeers.remove(peer);
  }

  @action addManager(manager) {
    this.selectedManagers.push(manager);
  }

  @action removeManager(manager) {
    this.selectedManagers.remove(manager);
  }

  @action updateManagerReviewer(manager) {
    this.selectedManagers = [manager];
  }

  @action async saveReviewee() {
    if (!this.selectedReviewee.employee) return;

    this.selectedReviewee.reviews = [];
    this.selectedReviewee.reviewManagerEmployees = this.selectedManagers;
    this.selectedReviewee.cycle = this.cycle;

    if (this.cycle.managerType) {
      this.selectedManagers.forEach(manager => {
        this.selectedReviewee.reviews.push(
          new PerformanceReviewReview({
            reviewer: manager,
            type: this.cycle.managerType
          })
        );
      });
    }

    if (this.cycle.peerType) {
      for (const peer of this.selectedPeers) {
        this.selectedReviewee.reviews.push(
          new PerformanceReviewReview({reviewer: peer, type: this.cycle.peerType})
        );
      }
    }

    const {model, errors} = await this.store.post(
      endpoints.PERFORMANCE_REVIEWS.REVIEWEES.ALL,
      types.PERFORMANCE_REVIEWS.REVIEWEE,
      this.selectedReviewee
    );

    this.errors = errors;
    await this.__updateCycle();

    if (model) {
      this.onSave(model);
    }
  }

  async __updateCycle() {
    await this.store._compose([
      endpoints.PERFORMANCE_REVIEWS.CYCLE.with(this.cycle.id)
    ]);

    const newCycle = new PerformanceReviewCycle(
      this.store._getSingle(types.PERFORMANCE_REVIEWS.CYCLE)
    );

    this.cycle.update(newCycle);
  }
}

export default AddRevieweeState;
